// src/hooks/useFarmProducts.js
// Hook to fetch, filter, and paginate products for the Farm marketplace.
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { productRepository } from '../repositories/productRepository';
import { isProductVisible, FARM_FETCH_LIMIT, FARM_PAGE_SIZE } from '../constants/farmConstants';
import { isItemExpired, extractExpiryDate } from '../utils/formatExpiration';
import { distanceInKm } from '../utils/distance';
import { AGRICULTURAL_CATEGORIES } from '../utils/agriculturalCategories';
import { isAnimalCategory } from '../utils/animalCategories';

/**
 * @param {Object} opts
 * @param {string} opts.searchTerm
 * @param {string} opts.category
 * @param {string} opts.sortOrder
 * @param {Object|null} opts.userCoords  - { lat, lng }
 * @param {boolean} opts.nearbyActive
 * @param {number} opts.nearbyRadiusKm
 * @returns {{ products, visible, page, hasMore, loadMore, loading, error, retry, total }}
 */
export default function useFarmProducts({
  searchTerm = '',
  category = '',
  sortOrder = 'newest',
  userCoords = null,
  nearbyActive = false,
} = {}) {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const docs = await productRepository.listRecent({ max: FARM_FETCH_LIMIT });
      if (!mountedRef.current) return;
      setAllProducts(docs);
    } catch (err) {
      if (mountedRef.current) setError(err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Filtered + sorted view
  const visible = useMemo(() => {
    const lower = (searchTerm || '').trim().toLowerCase();
    let list = allProducts.filter((p) => {
      // 1) must be visible (not sold/hidden/expired/draft/deleted)
      if (!isProductVisible(p)) return false;
      // 2) must not be expired
      if (isItemExpired(p)) return false;
      return true;
    });

    // Category filter
    if (category) {
      const normalizedSelected = normalizeCategoryToId(category);
      list = list.filter((p) => {
        const productCategory = normalizeCategoryToId(p.categoryId || p.category || '');
        return productCategory === normalizedSelected;
      });
    }

    // Search filter
    if (lower) {
      list = list.filter((p) => {
        const haystack = [
          p.title, p.name,
          p.category,
          p.poster?.displayName, p.poster?.name,
          p.location?.province, p.location?.district,
          p.location?.sector, p.location?.cell,
          p.desc, p.description,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(lower);
      });
    }

    const effectiveSort = nearbyActive ? 'closest' : sortOrder;

    switch (effectiveSort) {
      case 'priceLow':
        list.sort((a, b) => getProductPricePerKg(a) - getProductPricePerKg(b));
        break;
      case 'priceHigh':
        list.sort((a, b) => getProductPricePerKg(b) - getProductPricePerKg(a));
        break;
      case 'closest':
        if (userCoords) {
          list.sort((a, b) => {
            const distA = getProductDistance(a, userCoords);
            const distB = getProductDistance(b, userCoords);
            return distA - distB;
          });
        }
        break;
      default: // newest
        list.sort(
          (a, b) =>
            toMs(b.createdAt) - toMs(a.createdAt)
        );
    }

    return list;
  }, [allProducts, searchTerm, category, sortOrder, userCoords, nearbyActive]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [searchTerm, category, sortOrder, nearbyActive]);

  const paged = useMemo(() => visible.slice(0, page * FARM_PAGE_SIZE), [visible, page]);
  const hasMore = paged.length < visible.length;
  const loadMore = useCallback(() => setPage((p) => p + 1), []);

  return {
    products: allProducts,
    visible: paged,
    totalVisible: visible.length,
    total: allProducts.length,
    page,
    hasMore,
    loadMore,
    loading,
    error,
    retry: fetchProducts,
  };
}

// Helpers
function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (typeof ts === 'number') return ts;
  return new Date(ts).getTime() || 0;
}

function getProductDistance(product, userCoords) {
  const loc = product.location;
  if (!loc) return Infinity;
  const lat = loc.latitude ?? loc.lat;
  const lng = loc.longitude ?? loc.lng ?? loc.lon;
  if (lat == null || lng == null) return Infinity;
  return distanceInKm(
    userCoords,
    {lat:Number(lat), lng:Number(lng) }
  );
}

function normalizeCategoryToId(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';

  // Animal categories are isolated from the crop catalog; resolve them first
  // so they never leak into crop-only search or suggestions.
  if (isAnimalCategory(raw)) return raw;

  const byId = AGRICULTURAL_CATEGORIES.find((c) => c.id.toLowerCase() === raw);
  if (byId) return byId.id;

  const byName = AGRICULTURAL_CATEGORIES.find((c) => c.name.toLowerCase() === raw);
  if (byName) return byName.id;

  return raw;
}

function getProductPricePerKg(product) {
  const n = Number(product?.pricePerKg ?? product?.price ?? 0);
  return Number.isFinite(n) ? n : 0;
}
